// src/lib/section-content-utils.ts
// Helpers for the checkpoint review/edit experience:
//  - extractDataNeeded(): finds the AI's "[DATA NEEDED]" / "[… REQUIRED]" / "TBD"
//    placeholders inside a (possibly JSON) section so the UI can flag them in yellow.
//  - flattenEditableFields() / setFieldByPath(): turn a structured section JSON into a
//    flat list of labeled, human-readable text fields for editing, and write an edit
//    back into the structure (so we never make the user hand-edit raw JSON).

// Inline placeholders the generators emit when they lack real data, e.g.
// "[DATA NEEDED: Program Manager]", "[SIGNATURE REQUIRED]", "[INSERT date]".
export const DATA_NEEDED_RE = /\[[^\]]*?(?:DATA NEEDED|NEEDED|INSERT|PROVIDE|REQUIRED|TBD|TO BE PROVIDED)[^\]]*?\]/gi;

export interface DataNeededItem {
  marker: string; // the literal placeholder text, e.g. "[DATA NEEDED: PM]"
  field: string;  // a human-readable location, e.g. "Management › Key Roles #1 › Name"
}

// Parse a value that may be a JSON string, a JSON object, or plain prose.
export function parseMaybeJson(content: unknown): any {
  if (content && typeof content === 'object') return content;
  if (typeof content !== 'string') return null;
  try {
    const parsed = JSON.parse(content);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

// Title-case a single object key: "toolsAndTechnologies" → "Tools And Technologies".
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

// Render a path of keys/indices as "A › B #2 › C".
export function humanizePath(path: Array<string | number>): string {
  const parts: string[] = [];
  for (const seg of path) {
    if (typeof seg === 'number') {
      // attach the index to the previous segment as "#n"
      if (parts.length > 0) parts[parts.length - 1] += ` #${seg + 1}`;
      else parts.push(`#${seg + 1}`);
    } else {
      parts.push(humanizeKey(seg));
    }
  }
  return parts.join(' › ');
}

function isPlaceholderValue(s: string): boolean {
  const t = s.trim();
  return t === 'TBD' || /^tbd\b/i.test(t) || t.toLowerCase() === 'to be provided' || t.toLowerCase() === 'n/a';
}

// Walk a parsed section and return every "needs your data" placeholder we find,
// each with a readable location label. Deduped.
export function extractDataNeeded(content: unknown): DataNeededItem[] {
  const root = parseMaybeJson(content);
  const out: DataNeededItem[] = [];
  const seen = new Set<string>();

  const push = (marker: string, path: Array<string | number>) => {
    const field = humanizePath(path) || 'Section';
    const key = `${field}::${marker.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ marker: marker.trim(), field });
  };

  const scanString = (s: string, path: Array<string | number>) => {
    const matches = s.match(DATA_NEEDED_RE);
    if (matches) for (const m of matches) push(m, path);
    // A whole field that is just "TBD"/"N/A" is also a gap worth surfacing.
    if (isPlaceholderValue(s) && !matches) push(s.trim() || 'TBD', path);
  };

  const walk = (val: any, path: Array<string | number>) => {
    if (val == null) return;
    if (typeof val === 'string') {
      scanString(val, path);
    } else if (Array.isArray(val)) {
      val.forEach((item, i) => walk(item, [...path, i]));
    } else if (typeof val === 'object') {
      for (const [k, v] of Object.entries(val)) walk(v, [...path, k]);
    }
  };

  if (root) {
    walk(root, []);
  } else if (typeof content === 'string') {
    // Plain prose: still flag inline markers (no field path).
    const matches = content.match(DATA_NEEDED_RE);
    if (matches) for (const m of matches) push(m, []);
  }
  return out;
}

// ---- Field-by-field editing (structure-preserving) --------------------------

export type EditFieldKind = 'text' | 'list';

export interface EditField {
  path: Array<string | number>;
  label: string;
  kind: EditFieldKind;
  value: string;        // for 'list', the items joined by newlines
  flagged: boolean;     // contains a DATA_NEEDED / placeholder marker
}

function valueIsFlagged(s: string): boolean {
  DATA_NEEDED_RE.lastIndex = 0;
  return DATA_NEEDED_RE.test(s) || isPlaceholderValue(s);
}

// Flatten a structured section into the editable string/string[] leaves, in order,
// each with a readable label. Non-string scalars (numbers/booleans) are left alone
// (not exposed) so editing can't corrupt computed values.
export function flattenEditableFields(content: unknown): EditField[] {
  const root = parseMaybeJson(content);
  if (!root) return [];
  const fields: EditField[] = [];

  const walk = (val: any, path: Array<string | number>) => {
    if (val == null) return;
    if (typeof val === 'string') {
      fields.push({ path, label: humanizePath(path), kind: 'text', value: val, flagged: valueIsFlagged(val) });
    } else if (Array.isArray(val)) {
      const allStrings = val.length > 0 && val.every(v => typeof v === 'string');
      if (allStrings) {
        const joined = (val as string[]).join('\n');
        fields.push({ path, label: humanizePath(path), kind: 'list', value: joined, flagged: valueIsFlagged(joined) });
      } else {
        val.forEach((item, i) => walk(item, [...path, i]));
      }
    } else if (typeof val === 'object') {
      for (const [k, v] of Object.entries(val)) walk(v, [...path, k]);
    }
  };

  walk(root, []);
  return fields;
}

// Return a NEW content string with the field at `path` replaced. `kind: 'list'`
// splits the incoming text on newlines back into a string[]. Keeps everything else
// intact so the structured renderer still applies after an edit.
export function setFieldByPath(content: unknown, path: Array<string | number>, raw: string, kind: EditFieldKind): string {
  const root = parseMaybeJson(content);
  if (!root) return typeof content === 'string' ? content : '';
  const clone = JSON.parse(JSON.stringify(root));
  let node: any = clone;
  for (let i = 0; i < path.length - 1; i++) node = node?.[path[i]];
  const last = path[path.length - 1];
  if (node == null) return JSON.stringify(clone);
  if (kind === 'list') {
    node[last] = raw.split('\n').map(s => s.trim()).filter(Boolean);
  } else {
    node[last] = raw;
  }
  return JSON.stringify(clone);
}
